import { classNames } from '@fullstory/util/fmt';
import { Moment } from 'moment';
import React, { ReactNode, useContext } from 'react';
import { Box } from '../../../Box';
import { DateRangePickerContext } from '../../context';
import { getDayState } from '../../Day/util';
import { MomentRanges, RangeIndex } from '../../types';
import css from './index.module.scss';

export interface MinimapDayProps {
  /**
   * The day that will be rendered, must be a Moment object.
   */
  day: Moment;
  /**
   * The selected/comparison date ranges. The `MinimapDay` will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: MomentRanges;
  /**
   * Controls the which range takes priority when rendering overlapping ranges.
   */
  focusedRange: RangeIndex;
  /**
   * If `true`, day will be disabled and non-interactive. Useful for days that are
   * outside of the min/max dates or disabled for some other reason.
   */
  disabled?: boolean;
  /**
   * If provided, the day will be highlighted and an info tooltip will be displayed
   * on hover.
   */
  infoTooltip?: ReactNode;
}

const MinimapDay: React.FC<MinimapDayProps> = ({
  day,
  ranges,
  focusedRange,
  disabled = false,
  infoTooltip,
}) => {
  const { color, selected } = getDayState(day, ranges, focusedRange);
  const { readOnly } = useContext(DateRangePickerContext);
  return (
    <Box
      data-component="MinimapDay"
      width={3}
      height={2}
      className={classNames(css.minimapDay, css[color], {
        [css.selected]: selected,
        [css.disabled]: disabled || readOnly,
        [css.info]: !disabled && Boolean(infoTooltip),
      })}
    />
  );
};

const MemoizedMinimapDay = React.memo(MinimapDay);

export { MemoizedMinimapDay as MinimapDay };
